import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'oidc-client-ts';
import authService from '../services/AuthService';

interface UserPermissions {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canAdd: boolean;
}

interface AuthContextType {
  user: User | null;
  permissions: UserPermissions;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState<UserPermissions>({
    canView: false,
    canEdit: false,
    canDelete: false,
    canAdd: false
  });

  useEffect(() => {
    const initAuth = async () => {
      try {
        const user = await authService.getUser();
        if (user && !user.expired) {
          setUser(user);
          updatePermissions(user);
        }
      } catch (error) {
        console.error('Fehler beim Initialisieren der Authentifizierung:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const updatePermissions = (user: User | null) => {
    if (!user || !user.profile.groups) {
      setPermissions({
        canView: false,
        canEdit: false,
        canDelete: false,
        canAdd: false
      });
      return;
    }

    const groups = user.profile.groups as string[];
    const isAdmin = groups.includes('telefonbuch_admin');
    const isUser = groups.includes('telefonbuch_user') || isAdmin;
    const isViewer = groups.includes('telefonbuch_viewer') || isUser;

    setPermissions({
      canView: isViewer,
      canEdit: isUser,        // User und Admin können bearbeiten
      canDelete: isUser,      // User und Admin können löschen
      canAdd: isUser          // User und Admin können hinzufügen
    });
  };

  const login = () => {
    authService.login();
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setPermissions({
      canView: false,
      canEdit: false,
      canDelete: false,
      canAdd: false
    });
  };

  const value: AuthContextType = {
    user,
    permissions,
    isAuthenticated: user !== null && !user.expired,
    isLoading,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
